import React from "react";
import { PendingOrder, BulkCreateFields, StrategyFilter } from "../types";
import { RiskByCompanyId } from "../riskViolations";
import { hasViewAtHorizon, RawRecommendation, SuggestionMap } from "../recommendationSelectors";
import { LHF_CODES } from "../utils";
import { TableRow } from "./TableRow";
import styles from "../OrderRecommendationList.module.css";

const hasTriggeredSuggestion = (
  recs: RawRecommendation[] | null | undefined,
  isLhf: boolean,
): boolean =>
  (recs ?? [])
    .some(
      (r) =>
        r.suggestions != null &&
        Object.entries(r.suggestions as SuggestionMap).some(
          ([key, s]) => s?.trade_type != null && (isLhf ? LHF_CODES.has(key) : !LHF_CODES.has(key)),
        ),
    );

interface RecommendationTableProps {
  filteredOmsRows: PendingOrder[];
  isBulkEditing: boolean;
  bulkCreateState: Record<string, BulkCreateFields>;
  bulkCreateSnapshot: Record<string, BulkCreateFields>;
  handleBulkCreateChange: (companyId: number, strategyGroup: "LHF" | "CORE", field: keyof BulkCreateFields, value: BulkCreateFields[keyof BulkCreateFields]) => void;
  strategyFilter: StrategyFilter;
  errorCreateKeys: string[];
  riskByCompanyId: RiskByCompanyId;
  /** Patches the row after a CIO note is saved, so the icon updates without a refetch. */
  applyCioNote: (companyId: number, cioNote: string | null) => void;
}

export const RecommendationTable: React.FC<RecommendationTableProps> = ({
  filteredOmsRows,
  isBulkEditing,
  bulkCreateState,
  bulkCreateSnapshot,
  handleBulkCreateChange,
  strategyFilter,
  errorCreateKeys,
  riskByCompanyId,
  applyCioNote,
}) => {
  if (filteredOmsRows.length === 0) return null;

  return (
    <>
      {filteredOmsRows.map(order => {
        const showLhf  = (strategyFilter === "all" || strategyFilter === "lhf")     && (hasViewAtHorizon(order.recommendations, 5) || hasTriggeredSuggestion(order.recommendations, true));
        const showCore = (strategyFilter === "all" || strategyFilter === "non-lhf") && (hasViewAtHorizon(order.recommendations, 3) || hasTriggeredSuggestion(order.recommendations, false));
        const subRows: Array<"LHF" | "CORE"> = [
          ...(showLhf  ? ["LHF"  as const] : []),
          ...(showCore ? ["CORE" as const] : []),
        ];
        const rowCount = subRows.length || 1;

        return (
          <tbody key={order.id} className={`${styles["company-block"]} ${styles["rec-block"]}`}>
            {subRows.map((sg, subIdx) => (
              <TableRow key={`${order.id}-${sg}`}
                variant="rec" order={order} isFirst={subIdx === 0}
                rowSpan={rowCount} first={order} isBulkEditing={isBulkEditing}
                strategyGroup={sg} bulkCreateState={bulkCreateState}
                bulkCreateSnapshot={bulkCreateSnapshot}
                handleBulkCreateChange={handleBulkCreateChange}
                isErrorRow={errorCreateKeys.includes(`${order.company}_${sg}`)}
                riskByCompanyId={riskByCompanyId}
                applyCioNote={applyCioNote} />
            ))}
          </tbody>
        );
      })}
    </>
  );
};
